import { Box, Button, TextField, styled } from "@mui/material";
import { DateTime } from "luxon";
import React, { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import ClickToCopy from "~frontend/components/ClickToCopy";
import { FlexBox } from "~frontend/components/FlexBox";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import formatContactName from "~frontend/util/formatContactName";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

import { ActiveCampaignContact, CampaignForTexter } from "./types";

const Header = styled(Box)({
  fontSize: "14px",
  fontWeight: 700,
  marginBottom: "4px",
});

const Label = styled(Box)({
  fontSize: "12px",
  fontWeight: 600,
});

const DetailText = styled("span")({
  fontSize: "12px",
  fontWeight: "normal",
});

export default function ContactDetails({
  contactData,
  campaign,
  threadId,
  threadToPhoneNumber,
  handleSaveNote,
  mutationPending,
  unsavedContactNote,
  onChangeUnsavedContactNote,
}: {
  contactData: {
    formattedData: Record<string, string>;
    note?: ActiveCampaignContact["note"];
    firstName?: string | null;
    lastName?: string | null;
  };
  campaign?: CampaignForTexter;
  threadId: string;
  threadToPhoneNumber: string;
  handleSaveNote: () => void;
  mutationPending: boolean;
  unsavedContactNote: string | null;
  onChangeUnsavedContactNote: (val: string) => void;
}): ReactElement {
  const intl = useIntl();
  const { subaccountLink } = useSubaccount()!;

  const relativeLink = `/threads?thread=${threadId}&campaign_ids=${
    campaign?.id || ""
  }&contact_or_message_search=${encodeURIComponent(
    prettyPhoneNumber(threadToPhoneNumber)
  )}&context=${campaign?.id}`;

  const link = `${document.location.protocol}//${
    document.location.host
  }${subaccountLink(relativeLink)}`;

  return (
    <FlexBox>
      <Box sx={{ flex: 1 }}>
        <Header sx={{ fontSize: "14px", fontWeight: 700, marginBottom: "4px" }}>
          <FormattedMessage
            defaultMessage="Contact Details"
            description="Header for the panel of contact details in the thread controls"
          />
        </Header>
        <Label sx={{ fontSize: "12px", fontWeight: 600 }}>
          {formatContactName(contactData, {
            formatMessage: intl.formatMessage,
          })}
        </Label>
        <Label sx={{ fontSize: "12px", fontWeight: 600 }}>
          {prettyPhoneNumber(threadToPhoneNumber)}
        </Label>
        <Label sx={{ fontSize: "12px" }}>
          {Object.keys(contactData?.formattedData)
            .filter(key => !["first_name", "last_name"].includes(key))
            .map(key => (
              <React.Fragment key={key}>
                {key}:{" "}
                <DetailText>{contactData?.formattedData[key]}</DetailText>
                <br />
              </React.Fragment>
            ))}
        </Label>
        <Header sx={{ marginTop: "24px" }}>
          <FormattedMessage
            defaultMessage="Conversation Link"
            description="Header for the sharable conversation link"
          />
        </Header>
        <ClickToCopy text={link}>{link.substring(0, 25)}...</ClickToCopy>
      </Box>
      <Box sx={{ flex: 1 }}>
        <FlexBox>
          <Header sx={{ flex: 1 }}>
            <FormattedMessage
              defaultMessage="Conversation Notes"
              description="Header for the conversation notes input"
            />
          </Header>

          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSaveNote}
              disabled={mutationPending || unsavedContactNote == null}
            >
              Save
            </Button>
          </Box>
        </FlexBox>
        <TextField
          sx={{ paddingTop: "10px" }}
          multiline
          minRows={3}
          value={unsavedContactNote ?? contactData?.note?.text ?? ""}
          placeholder={intl.formatMessage({
            defaultMessage:
              "Take notes here to capture details about your conversation with this contact.",
            description: "Placeholder text in the Contact Notes input",
          })}
          onChange={e => onChangeUnsavedContactNote(e.target.value)}
          fullWidth
        />
        <Box>
          <DetailText>
            {contactData?.note?.updatedBy && (
              <>
                Updated by {contactData.note.updatedBy.fullName} at{" "}
                {DateTime.fromMillis(contactData.note.updatedAt).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </>
            )}
          </DetailText>
        </Box>
      </Box>
    </FlexBox>
  );
}
